import { QUERY_VERSION } from "@/constants/serviceQueryConstants";
import { loadConsoleSettings } from './consoleLogger';

export type LogLevel = 'info' | 'error' | 'warning' | 'query' | 'entry' | 'exit';
export type LogSource = 'serviceOperations:94' | 'serviceOperations:131' | 'useServicesQuery:215' | string;

export interface EnhancedLogEntry {
  timestamp: Date;
  level: LogLevel;
  source?: LogSource;
  message: string;
  details?: any;
  duration?: number; // For tracking time between entry and exit
  entryId?: string; // For correlating entry and exit points
}

const logStore: EnhancedLogEntry[] = [];
const MAX_LOGS = 1000;

/**
 * Format timestamp to display in Kenya timezone (GMT+3)
 */
export const formatTimestampGMT3 = (date: Date = new Date()): string => {
  // Kenya is GMT+3
  const options: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone: 'Africa/Nairobi'
  };
  
  return new Date(date).toLocaleTimeString('en-US', options);
};

/**
 * Generate a unique ID for correlating entry and exit points
 */
const generateEntryId = (): string => {
  return Math.random().toString(36).substring(2, 10);
};

/**
 * Add a log entry to the store
 */
export const addLog = (
  level: LogLevel,
  message: string,
  source?: LogSource,
  details?: any
): string => {
  const timestamp = new Date();
  const entryId = generateEntryId();
  
  const entry: EnhancedLogEntry = {
    timestamp,
    level,
    source,
    message,
    details,
    entryId
  };
  
  // Add to log store
  logStore.unshift(entry);
  
  // Trim log store if it gets too large
  if (logStore.length > MAX_LOGS) {
    logStore.pop();
  }
  
  // Also send to console for developer visibility
  const consoleMessage = `[${formatTimestampGMT3(timestamp)}] [${level.toUpperCase()}] ${source ? `[${source}] ` : ''}${message}`;
  
  switch (level) {
    case 'error':
      console.error(consoleMessage, details || '');
      break;
    case 'warning':
      console.warn(consoleMessage, details || '');
      break;
    default:
      console.log(consoleMessage, details || '');
      break;
  }
  
  return entryId;
};

/**
 * Log function entry point
 */
export const logEntry = (functionName: string, params?: any): string => {
  return addLog('entry', `Entering ${functionName}`, undefined, params);
};

/**
 * Log function exit point
 */
export const logExit = async (functionName: string, entryId: string, result?: any): Promise<void> => {
  const settings = await loadConsoleSettings();

  if (!settings.debugging?.logFunctionDuration) {
    addLog('exit', `Exiting ${functionName}`, undefined, result);
    return;
  }
  
  // Find the corresponding entry to calculate duration
  const entryLog = logStore.find(log => log.entryId === entryId && log.level === 'entry');
  
  if (entryLog) {
    const entryTime = entryLog.timestamp;
    const exitTime = new Date();
    const durationMs = exitTime.getTime() - entryTime.getTime();
    
    addLog('exit', `Exiting ${functionName} (${durationMs}ms)`, undefined, { 
      result,
      duration: durationMs
    });
  } else {
    addLog('exit', `Exiting ${functionName}`, undefined, result);
  }
};

/**
 * Log a query execution
 */
export const logQuery = (queryName: string, params: any): string => {
  return addLog('query', `Executing query: ${queryName}`, undefined, { 
    params,
    queryVersion: QUERY_VERSION
  });
};

/**
 * Get all logs
 */
export const getLogs = (): EnhancedLogEntry[] => {
  return [...logStore];
};

/**
 * Clear all logs
 */
export const clearLogs = (): void => {
  logStore.length = 0;
};

/**
 * Get the current QUERY_VERSION
 */
export const getQueryVersion = (): string => {
  return QUERY_VERSION;
};

/**
 * Initialize the enhanced logging system
 */
export const initEnhancedLogging = async (): Promise<void> => {
  const settings = await loadConsoleSettings();
  if (!settings.debugging?.enhancedLogging) {
    return;
  }
  // Log initialization with query version
  addLog('info', `Enhanced logging initialized with QUERY_VERSION: ${QUERY_VERSION}`);
  
  // Intercept console methods to capture logs
  const originalConsoleLog = console.log;
  const originalConsoleWarn = console.warn;
  const originalConsoleError = console.error;
  
  console.log = function(...args) {
    const message = args.map(arg => 
      typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
    ).join(' ');
    
    // Don't add to log store if this is our own logging to avoid duplication
    if (!message.includes('[INFO]') && 
        !message.includes('[ERROR]') && 
        !message.includes('[WARNING]') &&
        !message.includes('[QUERY]') &&
        !message.includes('[ENTRY]') &&
        !message.includes('[EXIT]')) {
      addLog('info', message);
    }
    
    originalConsoleLog.apply(console, args);
  };
  
  console.warn = function(...args) {
    const message = args.map(arg => 
      typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
    ).join(' ');
    
    if (!message.includes('[WARNING]')) {
      addLog('warning', message);
    }
    
    originalConsoleWarn.apply(console, args);
  };
  
  console.error = function(...args) {
    const message = args.map(arg => 
      typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
    ).join(' ');
    
    // Check if this is one of our key error points
    let source: LogSource | undefined = undefined;
    
    if (message.includes('serviceOperations.ts:94')) {
      source = 'serviceOperations:94';
    } else if (message.includes('serviceOperations.ts:131')) {
      source = 'serviceOperations:131';
    } else if (message.includes('useServicesQuery.ts:215')) {
      source = 'useServicesQuery:215';
    }
    
    if (!message.includes('[ERROR]')) {
      addLog('error', message, source);
    }
    
    originalConsoleError.apply(console, args);
  };
};
